import { useState, type ChangeEvent } from 'react';
import { Button, InputPlain } from '@peacecover/component';
import { ArrowLeft, CameraFill } from '@peacecover/icon';
import { useBettieProfileImageOverlay } from '../../chat/hooks/useBettieProfileImageOverlay';
import { useVirtualKeyboardVisible } from '../../chat/hooks/useVirtualKeyboardVisible';
import BettieProfileImageOverlay from '../../chat/components/BettieProfileImageOverlay';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';
import type { AgeGapType, Gender } from '../types';

const GENDER_OPTIONS: { label: string; value: Gender }[] = [
  { label: '여자', value: 'FEMALE' },
  { label: '남자', value: 'MALE' }
];
const AGE_OPTIONS: { label: string; value: AgeGapType }[] = [
  { label: '연하', value: 'YOUNGER' },
  { label: '동갑', value: 'SAME' },
  { label: '연상', value: 'OLDER' }
];

export type EnterUserInfoSubmitPayload = {
  personality?: string;
  ageGapType?: AgeGapType;
  gender?: Gender;
  nickname?: string;
  dataUrl?: string;
};

interface EnterUserInfoStepProps {
  initialNickname?: string;
  initialDataUrl?: string;
  onBack: () => void;
  onSubmit: (partial: EnterUserInfoSubmitPayload) => void;
}

const EnterUserInfoStep = ({
  initialNickname = '',
  initialDataUrl = '',
  onBack,
  onSubmit
}: EnterUserInfoStepProps) => {
  const [nickname, setNickname] = useState(initialNickname);
  const [dataUrl, setDataUrl] = useState(initialDataUrl);
  const [selectedGender, setSelectedGender] = useState<Gender | ''>('');
  const [selectedAge, setSelectedAge] = useState<AgeGapType | ''>('');
  const [relationship, setRelationship] = useState('');
  const { isKeyboardVisible, handleFocus, handleBlur } = useVirtualKeyboardVisible();

  const { openImagePicker, fileInputProps, overlayProps } = useBettieProfileImageOverlay({
    onConfirm: ({ dataUrl: url }) => setDataUrl(url)
  });

  const handleGenderSelect = (gender: Gender) => {
    setSelectedGender(gender);
  };

  const handleAgeSelect = (age: AgeGapType) => {
    setSelectedAge(age);
  };

  const handleSubmit = () => {
    const partial: EnterUserInfoSubmitPayload = {};
    if (selectedAge) partial.ageGapType = selectedAge;
    if (selectedGender) partial.gender = selectedGender;
    const personality = relationship.trim();
    if (personality) partial.personality = personality;
    const trimmedNickname = nickname.trim();
    if (trimmedNickname) partial.nickname = trimmedNickname;
    if (dataUrl) partial.dataUrl = dataUrl;
    onSubmit(partial);
  };

  return (
    <div className="pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] min-h-screen flex flex-col bg-background-dark">
      <header className="px-[16px] py-[15px]">
        <button type="button" onClick={onBack}>
          <ArrowLeft className="text-pure-white" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto pb-[120px]">
        <div className="flex flex-col items-start gap-[14px] px-[24px] py-[10px]">
          <h1 className="text-[26px] font-bold text-pure-white leading-[31px]">
            이야기에 등장할 <br />
            나의 정보를 입력해주세요
          </h1>
          <p className="text-[15px] font-normal text-main-light-4 leading-[18px]">
            이야기 속 나를 자유롭게 만들어보세요
          </p>
        </div>

        <div className="flex flex-col items-center gap-[24px] mt-[32px]">
          <input {...fileInputProps} />
          <div className="relative">
            <ProfileAvatar
              thumbnailUrl={dataUrl || undefined}
              widthAndHeight="82px"
              svgWidthAndHeight="56px"
              backgroundColor="#504D7F"
            />
            <button
              type="button"
              onClick={openImagePicker}
              className="bg-textPrimary-dark p-[2px] rounded-full w-fit h-fit border-[2px] border-pure-black absolute bottom-[-2px] right-[2px]"
            >
              <CameraFill size={14} className="text-pure-black" />
            </button>
          </div>
        </div>

        {/* Nickname Section */}
        <div className="mt-[24px] px-[24px]">
          <p className="text-[17px] font-semibold text-pure-white leading-[20px] mb-[12px]">이름</p>
          <div className="px-[16px] py-[17px] bg-secondary-dark rounded-[12px]">
            <InputPlain
              placeholder="이름을 입력해주세요"
              className="w-full bg-transparent text-[17px] font-semibold !text-main-dark-2 placeholder:text-placeholder-dark"
              value={nickname}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setNickname(e.target.value)}
            />
          </div>
        </div>

        {/* Gender Section */}
        <div className="mt-[28px] px-[24px]">
          <p className="text-[17px] font-semibold text-pure-white leading-[20px] mb-[12px]">성별</p>
          <div className="flex gap-[6px]">
            {GENDER_OPTIONS.map(({ label, value }) => (
              <button
                key={value}
                type="button"
                onClick={() => handleGenderSelect(value)}
                className={`flex-1 py-[16px] rounded-[8px] text-[17px] leading-[20px] font-semibold text-center transition-colors border
                   ${
                     selectedGender === value
                       ? 'border-pure-white text-pure-white'
                       : 'text-onDisabled-dark border-separator-dark-1'
                   }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Age Section */}
        <div className="mt-[32px] px-[24px]">
          <p className="text-[17px] font-semibold text-pure-white leading-[20px] mb-[12px]">
            나이
            <span className="ml-[6px] text-[13px] leading-[16px] text-main-light-4">
              (상대 등장인물을 기준으로 나이 관계를 선택)
            </span>
          </p>
          <div className="flex gap-[6px]">
            {AGE_OPTIONS.map(({ label, value }) => (
              <button
                key={value}
                type="button"
                onClick={() => handleAgeSelect(value)}
                className={`flex-1 py-[16px] rounded-[8px] text-[17px] leading-[20px] font-semibold text-center transition-colors border
                   ${
                     selectedAge === value
                       ? 'border-pure-white text-pure-white'
                       : 'text-onDisabled-dark border-separator-dark-1'
                   }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Relationship Section */}
        <div className="mt-[32px] px-[24px]">
          <p className="text-[17px] font-semibold text-pure-white leading-[20px] mb-[12px]">
            성격
            <span className="ml-[6px] text-[13px] leading-[16px] text-main-light-4">
              (ex. 귀여운, 활발한, 다정한, 무뚝뚝한 등)
            </span>
          </p>
          <div className="px-[16px] py-[17px] bg-secondary-dark rounded-[12px]">
            <InputPlain
              placeholder="여기에 입력하세요"
              className="w-full bg-transparent text-[17px] font-semibold !text-main-dark-2 placeholder:text-placeholder-dark"
              value={relationship}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setRelationship(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Footer Button */}
      <div
        className={`fixed bottom-0 left-0 right-0 px-[24px] w-full pt-[10px] pb-[calc(10px+env(safe-area-inset-bottom))] bg-background-dark transition-all duration-150 ease-in-out ${
          isKeyboardVisible
            ? 'translate-y-full opacity-0 pointer-events-none'
            : 'translate-y-0 opacity-100'
        }`}
        aria-hidden={isKeyboardVisible}
      >
        <Button size="large" className="w-full text-[17px] font-semibold" onClick={handleSubmit}>
          다음
        </Button>
      </div>
      <BettieProfileImageOverlay {...overlayProps} />
    </div>
  );
};

export default EnterUserInfoStep;
